import type { AbortedReason, CompletedJobOutcome } from '@/features/jobs/types/job-detail'
import type { Job, JobSeverity, JobType } from '@/features/jobs/types/job'
import type { SlaCategory } from '@/features/reports/lib/format-sla-category'

export type { CompletedJobOutcome, SlaCategory }

export type JobsReportBaseFilters = {
  date_raised: string
  job_type?: JobType
  location: string
  raised_by_user_id?: number
  assigned_partner_id?: number
  completed_at_from: string
  completed_at_to: string
  completed_by_user_id?: number
}

export type AbortedJobsReportFilters = JobsReportBaseFilters & {
  aborted_reason?: AbortedReason
}

export type CompletedJobsReportFilters = JobsReportBaseFilters & {
  outcome?: CompletedJobOutcome
}

export type ServiceLevelAgreementReportFilters = JobsReportBaseFilters & {
  severity?: JobSeverity
  sla_category?: SlaCategory
}

export type AbortedJob = Job & {
  assigned_partner_id: number
  aborted_reason: AbortedReason | null
  completed_at: string | null
}

export type CompletedJob = Job & {
  assigned_partner_id: number
  outcome: CompletedJobOutcome | null
  completed_at: string | null
}

export type AbortedReasonSummaryRow = {
  aborted_reason: AbortedReason
  count: number
}

export type CompletedOutcomeSummaryRow = {
  outcome: CompletedJobOutcome
  count: number
}

export type PartnerSummaryRow = {
  assigned_partner_id: number
  assigned_partner_name: string
  count: number
}

export type AbortedJobsReportSummary = {
  by_aborted_reason: AbortedReasonSummaryRow[]
}

export type CompletedJobsReportSummary = {
  by_outcome: CompletedOutcomeSummaryRow[]
}

export type JobsByPartnerReportSummary = {
  by_partner: PartnerSummaryRow[]
}

export type SlaJob = Job & {
  assigned_partner_id: number
  completed_at: string | null
  severity: JobSeverity
}

export type SlaCategorySummaryRow = {
  sla_category: SlaCategory
  count: number
}

export type ServiceLevelAgreementReportSummary = {
  by_sla_category: SlaCategorySummaryRow[]
}

const formatDateInputValue = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const currentCalendarMonthRange = () => {
  const now = new Date()
  const from = new Date(now.getFullYear(), now.getMonth(), 1)
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)

  return {
    completed_at_from: formatDateInputValue(from),
    completed_at_to: formatDateInputValue(to),
  }
}

export const emptyJobsReportBaseFilters = (): JobsReportBaseFilters => ({
  date_raised: '',
  job_type: undefined,
  location: '',
  raised_by_user_id: undefined,
  assigned_partner_id: undefined,
  ...currentCalendarMonthRange(),
  completed_by_user_id: undefined,
})

export const emptyAbortedJobsReportFilters = (): AbortedJobsReportFilters => ({
  ...emptyJobsReportBaseFilters(),
  aborted_reason: undefined,
})

export const emptyCompletedJobsReportFilters = (): CompletedJobsReportFilters => ({
  ...emptyJobsReportBaseFilters(),
  outcome: undefined,
})

export const emptyServiceLevelAgreementReportFilters =
  (): ServiceLevelAgreementReportFilters => ({
    ...emptyJobsReportBaseFilters(),
    severity: undefined,
    sla_category: undefined,
  })
